import {History, Logout, MoreHoriz, PersonOutline} from '@mui/icons-material';
import {Box, Divider, IconButton, List, Tooltip, Typography} from '@mui/material';
import {useLocation, useRouter} from '@tanstack/react-router';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'tolgee';
import {useLocalStorage} from 'usehooks-ts';

import {AuthService} from '~api/auth.service';
import {useLatestImageQuery} from '~api/images.queries';
import {useModelsQuery} from '~api/models.queries';
import {useCurrentProjectQuery} from '~api/projects.queries';
import {isInternalEmail} from '~utils/miscUtils';
import {useTrackEvent} from '~hooks/analytics.hooks';
import {useIsDeployAvailable} from '~hooks/useIsDeployAvailable';
import {selectIsFullscreen} from '~redux/reducers/annotatorReducer';
import {getGlobalLoadingType, getUtilStateIsLoading} from '~redux/reducers/globalReducer';
import {
  selectAvailableCustomers,
  selectCurrentProject,
  selectCurrentProjectId,
  selectUserEmail,
} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {IconMenuItem} from '~components/common/menu-items/IconMenuItem';
import {ToolbarMenu} from '~components/common/toolbar/ToolbarMenu';
import {ABOVE_ASK_AI_PANEL_Z_INDEX} from '~components/ask-ai/askAiAgents';
import {CustomerSwitcherDialog} from '~components/header/CustomerSwitcherDialog';
import {useLabelItems} from '~components/models/model.hooks';
import {UserSettings} from '~components/user/UserSettings';
import {styled, theme} from 'src/constants/theme';
import {
  ChevronRightIcon,
  DatabaseMenuItemIcon,
  DeployMenuItemIcon,
  MonitorMenuItemIcon,
  SettingsIcon,
  TrainMenuItemIcon,
} from 'src/icons';
import {getRoute, loginStatusQueryParam, replaceRoute, Route, RouteOptions} from '../../utils/routeUtil';
import {TextButton} from '../common/buttons/TextButton';
import {MainMenuItem} from './MainMenuItem';

const MaddoxAiLogo = styled('img')(({theme}) => ({
  margin: theme.spacing(4, 0, 5, 3),
  width: theme.spacing(4),
  height: theme.spacing(4),
  objectFit: 'contain',
}));

export function MainMenu(): JSX.Element | null {
  const {t} = useTranslation('mainMenu');
  const [openSettings, setOpenSettings] = useState(false);

  const projectLabelItems = useLabelItems({from: 'project'});
  const projectId = useAppSelector(selectCurrentProjectId);
  const activeProject = useAppSelector(selectCurrentProject);
  const userEmail = useAppSelector(selectUserEmail);

  const trackEvent = useTrackEvent();
  const router = useRouter();
  const location = useLocation();
  const [moreItemsActive, setMoreItemsActive] = useState(false);
  const moreItemsActivePaths = useMemo(() => [Route.settingsDevices, Route.projects, Route.changelog], []);
  const [isExpanded, setIsExpanded] = useLocalStorage('mainMenuExpanded', true);
  const [isCustomerSwitcherOpen, setIsCustomerSwitcherOpen] = useState(false);
  const availableCustomers = useAppSelector(selectAvailableCustomers);
  const showCustomerSwitcher = isInternalEmail(userEmail || '') && availableCustomers.length > 1;
  const isLoading = useAppSelector(getUtilStateIsLoading);
  const loadingType = useAppSelector(getGlobalLoadingType);
  const showAnimatedLogo = isLoading && loadingType === 'non-blocking';
  const isFullscreen = useAppSelector(selectIsFullscreen);
  const {isLoading: isProjectLoading, data: currentProject} = useCurrentProjectQuery();
  const {isLoading: isModelsLoading, data: models} = useModelsQuery();
  const {isDeployAvailable} = useIsDeployAvailable();

  const nonOkLabelItems = projectLabelItems.filter((label) => label.condition !== 'OK');
  // Get the latest image for the project to check if there are any machine predictions
  const {data: latestImage, isLoading: isLatestImageLoading} = useLatestImageQuery({
    projectId: currentProject?.id || projectId || '',
    params: {
      filters: {
        timeframe: undefined,
        machinePredictionLabels: nonOkLabelItems.map((label) => label.id),
      },
    },
    staleTime: 0,
    enabled: nonOkLabelItems.length > 0,
  });

  const routeOptions: RouteOptions = {keepModelIdQueryParam: false};

  const hasImages = useMemo(() => {
    if (isProjectLoading) return false;
    return currentProject?.hasImages ?? false;
  }, [isProjectLoading, currentProject]);

  const hasModels = useMemo(() => {
    if (isModelsLoading) return false;
    return models && models.length > 0;
  }, [isModelsLoading, models]);

  const hasMachinePredictions = useMemo(() => {
    if (nonOkLabelItems.length === 0) return false;
    if (isLatestImageLoading) return false;
    return !!latestImage;
  }, [isLatestImageLoading, latestImage, nonOkLabelItems.length]);

  useEffect(() => {
    setMoreItemsActive(
      moreItemsActivePaths && moreItemsActivePaths.some((pathname) => location.pathname.includes(pathname)),
    );
  }, [location.pathname, moreItemsActivePaths]);

  const toggleExpandedMenu = () => {
    setIsExpanded(!isExpanded);
  };

  function handleOpenSettings() {
    setOpenSettings(true);
  }
  function handleCloseSettings() {
    setOpenSettings(false);
  }

  async function handleLogout() {
    await AuthService.logout();
    trackEvent('logout');
    await replaceRoute(Route.index, {query: {[loginStatusQueryParam]: 'logout'}});
  }

  if (isFullscreen) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          width: isExpanded ? theme.spacing(24) : theme.spacing(10),
          backgroundColor: 'secondary.dark',
        }}
      >
        <Box display='flex' alignItems='center' gap={1}>
          <MaddoxAiLogo
            src={showAnimatedLogo ? '/gifs/maddox_logo_animated.gif' : '/images/maddox-ai-logo.svg'}
            alt='Maddox AI'
          />
          {isExpanded && (
            <Typography
              variant='h6'
              sx={{
                paddingBottom: theme.spacing(1.5),
                color: 'maddox.globalwhite',
                textTransform: 'uppercase',
                fontWeight: 300,
              }}
            >
              {t('companyName.maddox', {ns: 'common'})}
              <Box component='span' sx={{fontWeight: 700}}>
                {t('companyName.ai', {ns: 'common'})}
              </Box>
            </Typography>
          )}
        </Box>
        <List sx={{display: 'flex', flexDirection: 'column', height: '100%', padding: 0}}>
          <MainMenuItem
            onClick={() => trackEvent('main_menu_monitor')}
            linkTo={getRoute(Route.monitor, routeOptions)}
            text={t('monitor')}
            size='small'
            icon={MonitorMenuItemIcon}
            isEmpty={!hasMachinePredictions}
            expanded={isExpanded}
          />
          <MainMenuItem
            onClick={() => trackEvent('main_menu_deploy')}
            linkTo={getRoute(Route.deploy, routeOptions)}
            text={t('deploy')}
            icon={DeployMenuItemIcon}
            expanded={isExpanded}
            isEmpty={!isDeployAvailable}
            sx={{
              marginTop: theme.spacing(-1),
            }}
          />
          <MainMenuItem
            onClick={() => trackEvent('main_menu_models')}
            linkTo={getRoute(Route.models, routeOptions)}
            text={t('models')}
            icon={TrainMenuItemIcon}
            activePaths={['model']}
            expanded={isExpanded}
            isEmpty={!hasModels}
            sx={{
              marginTop: theme.spacing(-1),
            }}
          />
          <MainMenuItem
            onClick={() => trackEvent('main_menu_archive')}
            linkTo={getRoute(Route.archive, routeOptions)}
            text={t('archive')}
            icon={DatabaseMenuItemIcon}
            expanded={isExpanded}
            isEmpty={!hasImages}
            sx={{
              marginTop: theme.spacing(-1),
            }}
          />
          {/* More options menu item */}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              width: '100%',
              height: '100%',
              paddingLeft: theme.spacing(3),
            }}
          >
            <Tooltip title={isExpanded ? t('collapse') : t('expand')} arrow placement='right'>
              <IconButton
                size='small'
                sx={{
                  padding: 0,
                  color: 'maddox.globalwhite',
                  gap: theme.spacing(1),
                  '&:hover': {
                    outline: 0,
                    color: 'maddox.accent',
                    backgroundColor: 'transparent',
                  },
                }}
                onClick={toggleExpandedMenu}
              >
                <Box sx={{transform: isExpanded ? 'rotate(180deg)' : undefined}} height={24} width={24}>
                  <ChevronRightIcon />
                </Box>
                {isExpanded && (
                  <Typography variant='body1' sx={{fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer'}}>
                    {t('collapse')}
                  </Typography>
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            className={moreItemsActive ? 'active' : ''}
            sx={{
              marginTop: 'auto',
              marginBottom: 0,
              display: 'flex',
              width: '100%',
              padding: theme.spacing(2, 0, 2, 2.5),
              textDecoration: 'none',
              color: 'maddox.globalwhite',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: theme.spacing(1),
            }}
          >
            <ToolbarMenu
              id='main-menu-more-options'
              icon={<MoreHoriz />}
              tooltip={!isExpanded ? t('more', {ns: 'common'}) : undefined}
              placement='right'
              title={isExpanded ? t('more', {ns: 'common'}) : undefined}
              role='button'
              toolbarIconButtonStyles={{
                margin: 0,
                '&:hover': {
                  outline: 0,
                  color: 'maddox.accent',
                  backgroundColor: 'transparent',
                },
              }}
              toolbarButtonStyles={{
                margin: 0,
                fontSize: theme.typography.body1.fontSize,
                '&:hover': {
                  outline: 0,
                  color: 'maddox.accent',
                  backgroundColor: 'transparent',
                },
              }}
              sx={{
                zIndex: ABOVE_ASK_AI_PANEL_Z_INDEX,
                marginTop: 0,
                marginLeft: theme.spacing(7),
                '& .MuiPaper-root': {
                  width: '316px',
                  borderRadius: theme.spacing(1),
                  overflow: 'hidden',
                },
                '& .MuiList-root': {
                  paddingTop: 0,
                },
                '&:hover, &:focus, &.active, :focus-visible': {
                  outline: 0,
                  color: 'maddox.accent',
                  backgroundColor: 'transparent',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: theme.spacing(2),
                  padding: theme.spacing(2),
                  backgroundColor: 'background.default',
                  borderBottom: '1px solid',
                  borderColor: 'maddox.black20',
                }}
              >
                <Box
                  onClick={(event) => event.stopPropagation()}
                  sx={{display: 'flex', flexDirection: 'column', minWidth: 0, cursor: 'default'}}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 500,
                      lineHeight: '22px',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'maddox.black60',
                    }}
                  >
                    {t('activeProject')}
                  </Typography>
                  <Tooltip title={activeProject?.name ?? ''} arrow placement='top'>
                    <Typography noWrap sx={{fontSize: 14, lineHeight: '22px', color: 'maddox.black80'}}>
                      {activeProject?.name}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: theme.spacing(1)}}>
                  <TextButton
                    id='main-menu-switch-project'
                    variant='outlined'
                    size='small'
                    onClick={() => router.navigate({to: getRoute(Route.projects, routeOptions)})}
                    sx={{
                      width: '100%',
                      textTransform: 'none',
                      borderRadius: theme.spacing(0.5),
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: theme.palette.maddox.black20,
                      color: 'maddox.black80',
                      backgroundColor: 'maddox.globalwhite',
                      '&:hover': {
                        borderColor: theme.palette.maddox.black20,
                      },
                    }}
                  >
                    {t('projects')}
                  </TextButton>
                  {showCustomerSwitcher && (
                    <TextButton
                      id='main-menu-switch-customer'
                      variant='outlined'
                      size='small'
                      onClick={() => setIsCustomerSwitcherOpen(true)}
                      sx={{
                        width: '100%',
                        textTransform: 'none',
                        borderRadius: theme.spacing(0.5),
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: theme.palette.maddox.black20,
                        color: 'maddox.black80',
                        backgroundColor: 'maddox.globalwhite',
                        '&:hover': {
                          borderColor: theme.palette.maddox.black20,
                        },
                      }}
                    >
                      {t('switchCustomer')}
                    </TextButton>
                  )}
                </Box>
              </Box>
              <IconMenuItem
                id='main-menu-changelog'
                text={t('changelog')}
                icon={<History />}
                onClick={() => router.navigate({to: getRoute(Route.changelog, routeOptions)})}
              />
              <IconMenuItem
                id='main-menu-settings'
                text={t('settings')}
                icon={<SettingsIcon />}
                onClick={() => {
                  const route = getRoute(Route.settingsGeneral, routeOptions);
                  router.navigate({to: route});
                }}
              />
              <IconMenuItem
                id='main-menu-account-settings'
                text={t('userSettings')}
                icon={<PersonOutline />}
                onClick={handleOpenSettings}
              />
              <Divider
                sx={{
                  '&.MuiDivider-root': {
                    margin: 0,
                  },
                }}
              />
              <IconMenuItem
                id='main-menu-logout'
                text={t('logout')}
                icon={<Logout sx={{color: 'error.dark'}} />}
                onClick={handleLogout}
                sx={{color: 'error.dark'}}
              />
            </ToolbarMenu>
          </Box>
        </List>
      </Box>
      <UserSettings open={openSettings} onClose={handleCloseSettings} />
      {showCustomerSwitcher && (
        <CustomerSwitcherDialog open={isCustomerSwitcherOpen} onClose={() => setIsCustomerSwitcherOpen(false)} />
      )}
    </>
  );
}
